import { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { buildDataTableLabels } from "@/components/table";
import { useQuery } from "@tanstack/react-query";
import {
  Wallet,
  ArrowUpRight,
  ArrowDownLeft,
  Calendar,
  TrendingUp,
  RefreshCw,
  SlidersHorizontal,
} from "lucide-react";
import { useAuthStore } from "@/store/authStore";
import { activityService } from "@/services/activityService";
import { sellerService } from "@/services/sellerService";
import { walletService } from "@/services/walletService";
import { Button, Pagination, EmptyState } from "@/components/ui";
import {
  PageFrame,
  StatCard,
  TransactionItem,
  ListResultsCard,
  WalletHeroCard,
  WalletHeroChip,
} from "@/components/common";
import { DeferredFilterActions } from "@/components/common/DeferredFilterPanel";
import { WalletDateRangeInputs } from "@/components/common/WalletDateRangeInputs";
import { WalletTransactionFilterFields } from "@/components/common/WalletTransactionFilterFields";
import { useDeferredFilters } from "@/hooks/useDeferredFilters";
import {
  buildWalletTransactionFilterParams,
  countNonDateWalletFilters,
  filterWalletTransactionsClientSide,
  getDefaultWalletTransactionFilters,
} from "@/lib/walletTransactionFilters";
import { resolveCatalogTranslation } from "@/lib/translationResolution";
import { formatMoney } from "@/lib/money";
import type { Activity, SellerWallet } from "@/types";

const ITEMS_PER_PAGE = 10;

export default function WalletPage() {
  const { t: tCommon } = useTranslation("common");
  const tableLabels = buildDataTableLabels(tCommon);
  const { t, i18n } = useTranslation("wallet");
  const { t: tMovements } = useTranslation("components");
  const { isHost, isSalesAgent, getCurrentUserId } = useAuthStore();
  const isHostUser = isHost();
  const entityId = getCurrentUserId();

  const [currentPage, setCurrentPage] = useState(1);
  const [showFilters, setShowFilters] = useState(false);

  const {
    formFilters,
    appliedFilters,
    updateFormField,
    mergeFormFields,
    applyFilters,
    applyFilterPatch,
    resetFilters,
    hasUnappliedChanges,
  } = useDeferredFilters(getDefaultWalletTransactionFilters, {
    onApply: () => setCurrentPage(1),
    onReset: () => setCurrentPage(1),
  });

  const activeFiltersCount = useMemo(
    () => countNonDateWalletFilters(appliedFilters),
    [appliedFilters],
  );

  const filterParams = useMemo(
    () => buildWalletTransactionFilterParams(appliedFilters),
    [appliedFilters],
  );

  // Fetch activities for filter dropdown for all seller accounts.
  const { data: activities = [] } = useQuery<Activity[]>({
    queryKey: ["activities"],
    queryFn: () => activityService.getAll(),
    enabled: !!entityId && !isSalesAgent(),
  });

  // Fetch wallet data
  const {
    data: walletData,
    isLoading: isLoadingWallet,
    error: walletError,
    refetch: refetchWallet,
    isFetching: isRefetchingWallet,
  } = useQuery<SellerWallet>({
    queryKey: ["sellerWallet", entityId],
    queryFn: () => sellerService.getWallet(entityId!),
    enabled: !!entityId,
  });

  // Fetch wallet stats
  const { data: stats, isLoading: isLoadingStats } = useQuery({
    queryKey: ["sellerWalletStats", entityId, filterParams],
    queryFn: () => walletService.getStats(entityId!, filterParams),
    enabled: !!entityId,
  });

  // Fetch transactions with backend filters
  const {
    data: transactionsData,
    isLoading: isLoadingTransactions,
    refetch: refetchTransactions,
    isFetching: isFetchingTransactions,
  } = useQuery({
    queryKey: ["sellerWalletTransactions", entityId, filterParams, currentPage],
    queryFn: () =>
      walletService.getTransactions(entityId!, {
        ...filterParams,
        skip: (currentPage - 1) * ITEMS_PER_PAGE,
        limit: ITEMS_PER_PAGE * 10,
      }),
    enabled: !!entityId,
  });

  const filteredTransactions = useMemo(() => {
    const items = transactionsData?.items ?? [];
    return filterWalletTransactionsClientSide(
      items,
      appliedFilters,
      tMovements,
    );
  }, [transactionsData, appliedFilters, tMovements]);

  // Paginate filtered results
  const paginatedTransactions = useMemo(() => {
    const start = (currentPage - 1) * ITEMS_PER_PAGE;
    return filteredTransactions.slice(start, start + ITEMS_PER_PAGE);
  }, [filteredTransactions, currentPage]);

  const totalPages = Math.ceil(filteredTransactions.length / ITEMS_PER_PAGE);
  const walletBalance = walletData?.wallet_balance ?? 0;
  const isAmountOwed = walletBalance < 0;

  const entityMissingMessage = isHostUser
    ? t("hostIdMissing")
    : isSalesAgent()
      ? t("salesAgentIdMissing")
      : t("sellerIdMissing");
  const isPageLoading = !!entityId && (isLoadingWallet || isLoadingStats);
  const pageError = !entityId || !!walletError;
  const pageErrorMessage = !entityId
    ? entityMissingMessage
    : walletError
      ? t("errorLoad")
      : undefined;

  return (
    <PageFrame
      title={t("title")}
      description={entityId ? t("descriptionFull") : t("descriptionDefault")}
      icon={Wallet}
      isLoading={isPageLoading}
      loadingMessage={t("loadingBalance")}
      error={pageError}
      errorMessage={pageErrorMessage}
      onRetry={
        walletError
          ? () => {
              void refetchWallet();
            }
          : undefined
      }
      isRetrying={isRefetchingWallet}
    >
      <div className="flex flex-wrap items-center gap-3">
        <span className="text-surface-700 inline-flex items-center gap-2 text-sm font-medium">
          <Calendar
            className="text-surface-500 h-4 w-4"
            aria-hidden
          />
          {t("periodLabel")}
        </span>
        <WalletDateRangeInputs
          startDate={appliedFilters.startDate}
          endDate={appliedFilters.endDate}
          onChange={applyFilterPatch}
          className="flex flex-wrap items-center gap-2"
          inputClassName="w-auto"
        />
      </div>

      <div className="grid grid-cols-2 gap-3 sm:gap-4 xl:grid-cols-4">
        <WalletHeroCard
          className="col-span-2"
          label={isAmountOwed ? t("statAmountOwed") : t("statCurrentBalance")}
          amount={formatMoney(Math.abs(walletBalance))}
          isAmountOwed={isAmountOwed}
          meta={
            <WalletHeroChip>
              {t("statTransactions", {
                count: stats?.transactions_count ?? 0,
              })}
            </WalletHeroChip>
          }
        />
        <StatCard
          title={t("statTotalIncome")}
          value={formatMoney(stats?.total_credits || 0)}
          icon={ArrowDownLeft}
          iconColor="success"
        />
        <StatCard
          title={t("statTotalExpenses")}
          value={formatMoney(stats?.total_debits || 0)}
          icon={ArrowUpRight}
          iconColor="accent"
        />
      </div>

      <ListResultsCard
        title={
          <span className="flex items-center gap-2.5">
            <span className="border-surface-200 bg-surface-50 text-surface-600 flex h-7 w-7 shrink-0 items-center justify-center rounded-md border">
              <TrendingUp
                className="h-4 w-4"
                aria-hidden
              />
            </span>
            {t("transactionsTitle")}
          </span>
        }
        headerAction={
          <div className="flex items-center gap-2">
            <span className="bg-surface-100 text-surface-600 hidden rounded-md px-2 py-0.5 text-xs font-medium tabular-nums sm:inline-flex">
              {t("results", { count: filteredTransactions.length })}
            </span>
            <Button
              variant={showFilters ? "secondary" : "outline"}
              size="sm"
              aria-expanded={showFilters}
              onClick={() => setShowFilters((current) => !current)}
              leftIcon={<SlidersHorizontal className="h-4 w-4" />}
            >
              {t("filtersTitle")}
              {activeFiltersCount > 0 ? (
                <span className="bg-primary-fill rounded-full px-1.5 text-[11px] leading-4 font-semibold text-white tabular-nums">
                  {activeFiltersCount}
                </span>
              ) : null}
            </Button>
            <Button
              variant="outline"
              size="icon-sm"
              isLoading={isFetchingTransactions}
              aria-label={tCommon("refresh")}
              title={tCommon("refresh")}
              onClick={() => void refetchTransactions()}
            >
              {isFetchingTransactions ? null : (
                <RefreshCw
                  className="h-4 w-4"
                  aria-hidden
                />
              )}
            </Button>
          </div>
        }
        beforeContent={
          showFilters ? (
            <div className="border-surface-200 bg-surface-50 dark:bg-surface-100/30 border-t px-5 py-4">
              <p className="text-surface-500 mb-4 text-[13px]">
                {activeFiltersCount > 0
                  ? t("filtersActive", { count: activeFiltersCount })
                  : t("filtersHint")}
              </p>
              <WalletTransactionFilterFields
                filters={formFilters}
                labels={{
                  dateRange: t("dateRange"),
                  from: t("from"),
                  to: t("to"),
                  searchCriteria: t("searchCriteria"),
                  searchDescriptionLabel: t("searchDescriptionLabel"),
                  searchDescriptionPlaceholder: t("searchDescriptionPlaceholder"),
                  activityLabel: t("activityLabel"),
                  allActivities: t("allActivities"),
                  amountRange: t("amountRange"),
                  minAmount: t("minAmount"),
                  maxAmount: t("maxAmount"),
                  placeholderMin: t("placeholderMin"),
                  placeholderMax: t("placeholderMax"),
                }}
                onUpdateField={updateFormField}
                onUpdateDateRange={mergeFormFields}
                activities={activities.map((activity) => ({
                  value: activity.id,
                  label: resolveCatalogTranslation(
                    activity.name_translations,
                    i18n.language,
                  ),
                }))}
                inputClassName="bg-surface-0"
                showDateRange={false}
              />
              <DeferredFilterActions
                hasUnappliedChanges={hasUnappliedChanges}
                hasActiveFilters={activeFiltersCount > 0}
                onApply={applyFilters}
                onReset={resetFilters}
                applyLabel={t("applyFilters")}
                applyLabelUnchanged={t("searchTransactions")}
                resetLabel={t("clearFilters")}
                unsavedHint={t("unsavedFilters")}
                resetVariant="ghost"
                className="mt-4"
              />
            </div>
          ) : null
        }
        contentClassName="px-5 py-4"
        isLoading={isLoadingTransactions}
        loadingMessage={t("loadingTransactions")}
        isEmpty={!isLoadingTransactions && paginatedTransactions.length === 0}
        emptyState={
          <EmptyState
            icon={Wallet}
            title={t("emptyTitle")}
            description={
              appliedFilters.descriptionFilter ||
              appliedFilters.minAmount ||
              appliedFilters.maxAmount ||
              appliedFilters.selectedActivity
                ? t("emptyDescFiltered")
                : t("emptyDescDefault")
            }
          />
        }
        pagination={
          totalPages > 1 ? (
            <Pagination
              labels={tableLabels}
              className="mt-6"
              page={currentPage}
              totalPages={totalPages}
              onPageChange={setCurrentPage}
              showPageNumbers
            />
          ) : null
        }
      >
        <div className="space-y-2">
          {paginatedTransactions.map((transaction) => (
            <TransactionItem
              key={transaction.id}
              transaction={transaction}
            />
          ))}
        </div>
      </ListResultsCard>
    </PageFrame>
  );
}
